import { InternalAxiosRequestConfig } from "axios";
import { defaultLocale, isValidLocale } from "../../../i18n";
import { RequestInterceptor } from "../types/http-types";

/**
 * Function to get current locale from browser (client-side) or cookies
 * Extracted from api-client.ts to maintain exact same logic
 */
function getCurrentLocale(): string {
  if (typeof window === "undefined") {
    // Server-side: can't access window or Next.js useLocale
    return defaultLocale;
  }

  // Client-side: try to get from URL first, then cookies
  const path = window.location.pathname;
  const localeFromPath = path.split("/")[1]; // Get first segment after '/'

  if (isValidLocale(localeFromPath)) {
    return localeFromPath;
  }

  // Fallback to cookie
  const cookies = document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>
  );

  const cookieLocale = cookies["NEXT_LOCALE"];
  if (cookieLocale && isValidLocale(cookieLocale)) {
    return cookieLocale;
  }

  return defaultLocale;
}

/**
 * Locale Request Interceptor
 * Handles Accept-Language headers for internationalization
 * Follows SRP: Single responsibility - locale management only
 */
export class LocaleInterceptor implements RequestInterceptor {
  /**
   * Add Accept-Language header based on current locale
   * This ensures the backend can detect user language for email notifications
   */
  async onRequest(
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> {
    const currentLocale = getCurrentLocale();
    if (currentLocale) {
      config.headers["Accept-Language"] = `${currentLocale},en;q=0.9`;
    }

    return config;
  }

  onRequestError(error: any) {
    return Promise.reject(error);
  }
}

/**
 * Singleton instance for use across HTTP clients
 */
export const localeInterceptor = new LocaleInterceptor();

/**
 * Export getCurrentLocale for potential use in other parts of the application
 */
export { getCurrentLocale };
